// Compact platform pill used in landing-pages, UTM Health, and any table
// that surfaces a canonical platform. The brand glyph + name sit on a
// platform-specific tinted chip so two rows for different platforms never
// blend visually. Single source of truth for platform tokens — any place
// rendering a platform label should import this (not re-roll its own
// palette).

import { cn } from "@/lib/utils";
import type { CanonicalPlatform } from "@/lib/api";

const FA_BRAND_BY_PLATFORM: Record<CanonicalPlatform, string | null> = {
  Instagram: "fa-brands fa-instagram",
  Facebook: "fa-brands fa-facebook",
  LinkedIn: "fa-brands fa-linkedin",
  TikTok: "fa-brands fa-tiktok",
  Pinterest: "fa-brands fa-pinterest",
  YouTube: "fa-brands fa-youtube",
  Threads: "fa-brands fa-threads",
  X: "fa-brands fa-x-twitter",
  "Google Business": "fa-brands fa-google",
  "Other Social": null,
};

// Tailwind class fragments per platform. Background + text + ring go
// together so switching a single row only needs one map entry. Kept in
// string form (not composed from tokens) so Tailwind's JIT picks them up.
const TOKENS_BY_PLATFORM: Record<CanonicalPlatform, string> = {
  Instagram: "bg-fuchsia-50 text-fuchsia-700 ring-fuchsia-200",
  // SKE-3429 — `blue-*` is aliased to the brand-purple ramp in
  // tailwind.config.ts, which made Facebook visually identical to the
  // `Other Social` purple chip below. Use a darker shade of the
  // un-aliased `sky-*` palette so Facebook stays in the blue family
  // without colliding with LinkedIn's sky-50/700/200 chip.
  Facebook: "bg-sky-100 text-sky-800 ring-sky-300",
  LinkedIn: "bg-sky-50 text-sky-700 ring-sky-200",
  // TikTok's signature aesthetic is black with cyan/magenta accents. The
  // chip leans into the dark-on-light contrast so it reads as "TikTok"
  // at a glance without relying on the icon alone.
  TikTok: "bg-neutral-950 text-white ring-neutral-800",
  Pinterest: "bg-rose-50 text-rose-700 ring-rose-200",
  YouTube: "bg-red-50 text-red-700 ring-red-200",
  Threads: "bg-neutral-100 text-neutral-900 ring-neutral-300",
  // X rebrand: pure black brand. Inverted chip matches brand treatment
  // elsewhere (their own UI is monochrome).
  X: "bg-neutral-900 text-white ring-neutral-700",
  "Google Business": "bg-amber-50 text-amber-700 ring-amber-200",
  // Fallback aligns with the in-app brand purple so unclassified rows
  // still read as "a platform" without looking orphaned.
  "Other Social": "bg-purple-50 text-purple-700 ring-purple-100",
};

export function PlatformBadge({ platform }: { platform: CanonicalPlatform }) {
  const icon = FA_BRAND_BY_PLATFORM[platform];
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-semibold ring-1",
        TOKENS_BY_PLATFORM[platform],
      )}
    >
      {icon ? <i className={`${icon} text-[11px]`} aria-hidden="true" /> : null}
      <span>{platform}</span>
    </span>
  );
}
